import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import NewsletterOptIn from '../components/NewsletterOptIn';
import { AlertCircle, CheckCircle2, Users } from 'lucide-react';
import { isProgramPublic } from '../utils/programVisibility';
import { isPrivateLesson, getSessionOptions } from '../utils/privateLesson';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Registration/checkout page — noindexed (transactional, keyed by a class id
// in the query string, no evergreen content value). Self-referencing
// canonical still applies in case a crawler ignores noindex.
const RegisterClassSEO = () => (
  <SEOHead title="Class Registration" canonicalPath="/register-class" noindex />
);

export const RegisterClass = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId = searchParams.get('class');
  
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollmentData, setEnrollmentData] = useState(null);
  
  const [formData, setFormData] = useState({
    // Student Info
    studentFirstName: '',
    studentLastName: '',
    studentAge: '',
    studentGrade: '',
    // Parent/Guardian Info
    parentFirstName: '',
    parentLastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'TX',
    zip: '',
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    // Additional Info
    allergies: '',
    medicalConditions: '',
    specialRequirements: '',
    howHeard: '',
    newsletterOptIn: false,
    // Private lessons only
    sessionOption: '',
    preferredTimes: ''
  });
  const [showPayPal, setShowPayPal] = useState(false);

  // Fetch programs from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/programs`);
        if (res.ok) {
          const data = await res.json();
          setPrograms(data);
        }
      } catch (err) {
        console.error('Error fetching programs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get current class using useMemo
  const classProgram = useMemo(() => {
    return programs.find(p => p.id === parseInt(classId));
  }, [programs, classId]);

  // Check enrollment capacity (never applies to one-on-one private lessons)
  useEffect(() => {
    const checkCapacity = async () => {
      if (classProgram && classProgram.class_type === 'enrollment_managed' && !isPrivateLesson(classProgram)) {
        try {
          // Public capacity check endpoint
          const res = await fetch(`${API_URL}/api/content/programs/${classId}/capacity`);
          if (res.ok) {
            setEnrollmentData(await res.json());
          }
        } catch (err) {
          console.error('Error checking capacity:', err);
        }
      }
    };
    if (classProgram) {
      checkCapacity();
    }
  }, [classProgram, classId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setShowPayPal(false);
  };

  const isPrivate = isPrivateLesson(classProgram);
  const sessionOptions = isPrivate ? getSessionOptions(classProgram) : [];
  const selectedSession = sessionOptions.find(o => o.label === formData.sessionOption);

  const getPrice = () => {
    if (!classProgram) return 0;
    if (isPrivate) return Number(selectedSession?.price) || 0;
    return classProgram.price || 0;
  };

  const getClassItems = () => {
    if (!classProgram) return [];
    return [{
      name: isPrivate ? `${classProgram.title} — ${formData.sessionOption}` : classProgram.title,
      description: isPrivate
        ? `Private lesson with ${classProgram.instructor} (${formData.sessionOption}) — time arranged after booking`
        : `Class with ${classProgram.instructor} - ${classProgram.schedule}`,
      quantity: 1,
      price: getPrice(),
      category: 'class'
    }];
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Check if class is full
    if (enrollmentData && enrollmentData.spots_available !== null && enrollmentData.spots_available <= 0) {
      toast.error('Sorry, this class is currently full');
      return;
    }
    
    setShowPayPal(true);
  };

  const handlePaymentSuccess = (result) => {
    // Store registration data for confirmation
    const registrationData = {
      ...formData,
      classId: classProgram.id,
      classTitle: classProgram.title,
      instructor: classProgram.instructor,
      schedule: classProgram.schedule,
      price: getPrice(),
      paymentId: result.payment_id,
      status: 'paid',
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(`class_registration_${Date.now()}`, JSON.stringify(registrationData));
    
    toast.success('Registration complete!', {
      description: `${formData.studentFirstName} is now enrolled in ${classProgram.title}. Check your email for confirmation.`
    });
    
    // Redirect based on class category
    setTimeout(() => {
      if (classProgram.category === 'Youth') {
        navigate('/classes/kids');
      } else {
        navigate('/classes/adult');
      }
    }, 2000);
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Payment error:', error);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterClassSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-600">Loading class details...</p>
        </div>
      </div>
    );
  }

  if (!classProgram) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterClassSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Class Not Found</h1>
          <p className="text-stone-600 mb-6">The class you are looking for does not exist or registration is not available.</p>
          <Button onClick={() => navigate('/classes/kids')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  if (!isProgramPublic(classProgram)) {
    const isDraft = (classProgram.status || 'active') === 'draft';
    return (
      <div className="min-h-screen bg-white py-16" data-testid="class-paused-message">
        <RegisterClassSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">{classProgram.title}</h1>
          <span className="inline-block px-3 py-1 mb-4 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
            {isDraft ? 'Not Yet Open' : 'Currently Paused'}
          </span>
          <p className="text-stone-600 mb-6">
            {isDraft
              ? 'This class is not open for registration yet. Please check back soon.'
              : 'This class is currently paused and is expected to return at a later date. Online registration is not available.'}
          </p>
          <Button onClick={() => navigate('/classes')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  // Check if class allows online payment (enrollment_managed OR PayPal payment method)
  const isPayable = classProgram &&
    (classProgram.class_type === 'enrollment_managed' || classProgram.payment_method === 'paypal' || isPrivate);

  const isYouthClass = classProgram?.category === 'Youth';

  // Determine required form fields based on class type
  const validateForm = () => {
    if (isPrivate) {
      if (!formData.sessionOption) {
        toast.error('Please choose how long you would like the session to be');
        return false;
      }
      if (!formData.preferredTimes?.trim()) {
        toast.error('Please tell us your preferred days & times so we can schedule the lesson');
        return false;
      }
    }
    if (isYouthClass) {
      // Full youth form validation
      const required = [
        'studentFirstName', 'studentLastName',
        'parentFirstName', 'parentLastName',
        'email', 'phone',
        'emergencyContactName', 'emergencyContactPhone'
      ];
      for (const field of required) {
        if (!formData[field]?.trim()) {
          toast.error('Please fill in all required fields');
          return false;
        }
      }
    } else {
      // Adult class — just name/email/phone
      const required = ['parentFirstName', 'parentLastName', 'email', 'phone'];
      for (const field of required) {
        if (!formData[field]?.trim()) {
          toast.error('Please fill in all required fields');
          return false;
        }
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  // Guard: class must support online payment
  if (!isPayable) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterClassSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Registration Not Available</h1>
          <p className="text-stone-600 mb-6">Online registration is not available for this class. Please contact us for more information.</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate(isYouthClass ? '/classes/kids' : '/classes/adult')} variant="outline">
              Back to Classes
            </Button>
            <Button onClick={() => navigate('/contact')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isFull = enrollmentData && enrollmentData.spots_available !== null && enrollmentData.spots_available <= 0;

  return (
    <div className="min-h-screen bg-white" data-testid="class-register-page">
      <RegisterClassSEO />
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            onClick={() => navigate(classProgram.category === 'Youth' ? '/classes/kids' : '/classes/adult')} 
            variant="outline" 
            className="mb-6 border-stone-300 text-stone-700 hover:bg-stone-50"
          >
            ← Back to Classes
          </Button>
          <h1 className="text-4xl font-serif text-stone-900 mb-4">{isPrivate ? 'Book a Private Lesson' : 'Class Registration'}</h1>
          <p className="text-xl text-stone-600">{isPrivate ? classProgram.title : `Enroll in ${classProgram.title}`}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Class Details Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border-stone-200 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-xl font-serif">Class Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-stone-900 mb-1">{classProgram.title}</h3>
                    <p className="text-sm text-cafa-red">{classProgram.instructor}</p>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-stone-700">Level:</span>
                      <p className="text-stone-600">{classProgram.level}</p>
                    </div>
                    {!isPrivate && (
                      <div>
                        <span className="font-semibold text-stone-700">Duration:</span>
                        <p className="text-stone-600">{classProgram.duration}</p>
                      </div>
                    )}
                    <div>
                      <span className="font-semibold text-stone-700">Schedule:</span>
                      <p className="text-stone-600">
                        {isPrivate
                          ? 'By appointment — we\u2019ll contact you to arrange a time that suits you'
                          : classProgram.schedule}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-stone-700">Location:</span>
                      <p className="text-stone-600">115 Melrose Place, San Antonio, TX</p>
                    </div>
                  </div>

                  {/* Enrollment Status */}
                  {enrollmentData && (
                    <div className="pt-4 border-t border-stone-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Users size={16} className="text-stone-500" />
                        <span className="text-sm font-semibold text-stone-700">Enrollment Status</span>
                      </div>
                      {isFull ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded">
                          <AlertCircle size={16} />
                          <span className="text-sm">Class is currently full</span>
                        </div>
                      ) : enrollmentData.spots_available !== null ? (
                        <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-2 rounded">
                          <CheckCircle2 size={16} />
                          <span className="text-sm">{enrollmentData.spots_available} spots available</span>
                        </div>
                      ) : (
                        <div className="text-sm text-stone-600">Spots available</div>
                      )}
                    </div>
                  )}

                  <div className="pt-4 border-t border-stone-200">
                    <p className="text-sm font-semibold text-stone-700 mb-2">Tuition:</p>
                    {isPrivate ? (
                      <>
                        <p className="text-2xl font-bold text-cafa-red">
                          {selectedSession ? `$${selectedSession.price}` : `From $${Math.min(...sessionOptions.map(o => Number(o.price) || 0))}`}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">
                          {selectedSession ? `Per ${selectedSession.label} session` : 'Choose a session length in the form'}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-cafa-red">${classProgram.price}</p>
                        <p className="text-xs text-stone-500 mt-1">Per {classProgram.duration?.toLowerCase().includes('week') ? 'session' : 'class'}</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Registration Form */}
            <div className="lg:col-span-2">
              {isFull ? (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-8 text-center">
                    <AlertCircle size={48} className="mx-auto mb-4 text-amber-500" />
                    <h2 className="text-xl font-semibold text-stone-900 mb-2">Class is Full</h2>
                    <p className="text-stone-600 mb-6">
                      This class is currently at capacity. Please contact us to be added to the waitlist.
                    </p>
                    <Button onClick={() => navigate('/contact')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                      Contact Us for Waitlist
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-stone-200">
                  <CardHeader>
                    <CardTitle className="text-xl font-serif">
                      {isYouthClass ? 'Student & Parent Information' : 'Your Information'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleProceedToPayment} className="space-y-6">
                      {isPrivate && (
                        <div className="border border-cafa-red/30 bg-cafa-red-50 rounded-lg p-4 space-y-4" data-testid="private-lesson-booking-section">
                          <div>
                            <h3 className="font-semibold text-stone-900">Private Lesson Booking</h3>
                            <p className="text-sm text-stone-600 mt-0.5">
                              Pick a session length and tell us when you&apos;re free. We&apos;ll confirm the exact date and time with you by email after booking.
                            </p>
                          </div>
                          <div>
                            <Label>Session length *</Label>
                            <div className="mt-2 space-y-2">
                              {sessionOptions.map((opt) => (
                                <label
                                  key={opt.label}
                                  className={`flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                    formData.sessionOption === opt.label
                                      ? 'border-cafa-red bg-white'
                                      : 'border-stone-300 bg-white/60 hover:border-cafa-red/50'
                                  }`}
                                  data-testid={`session-option-${opt.label.replace(/\s+/g, '-')}`}
                                >
                                  <span className="flex items-center gap-3">
                                    <input
                                      type="radio"
                                      name="sessionOption"
                                      value={opt.label}
                                      checked={formData.sessionOption === opt.label}
                                      onChange={handleChange}
                                      className="accent-cafa-red"
                                    />
                                    <span className="font-medium text-stone-800">{opt.label}</span>
                                  </span>
                                  <span className="font-bold text-cafa-red">${opt.price}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="preferredTimes">Preferred days &amp; times *</Label>
                            <Textarea
                              id="preferredTimes"
                              name="preferredTimes"
                              value={formData.preferredTimes}
                              onChange={handleChange}
                              placeholder="e.g. Weekday afternoons after 3pm, or Saturday mornings"
                              className="mt-1 bg-white"
                              rows={2}
                              data-testid="preferred-times-input"
                            />
                          </div>
                        </div>
                      )}
                      {isYouthClass ? (
                        <>
                      {/* Student Information */}
                      <div className="bg-cafa-red-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-stone-900 mb-4">Student Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="studentFirstName">Student First Name *</Label>
                            <Input
                              id="studentFirstName"
                              name="studentFirstName"
                              value={formData.studentFirstName}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="studentLastName">Student Last Name *</Label>
                            <Input
                              id="studentLastName"
                              name="studentLastName"
                              value={formData.studentLastName}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="studentAge">Student Age</Label>
                            <Input
                              id="studentAge"
                              name="studentAge"
                              type="number"
                              min="5"
                              max="18"
                              value={formData.studentAge}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="studentGrade">Grade Level</Label>
                            <select
                              id="studentGrade"
                              name="studentGrade"
                              value={formData.studentGrade}
                              onChange={handleChange}
                              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                            >
                              <option value="">Select Grade</option>
                              <option value="K">Kindergarten</option>
                              <option value="1">1st Grade</option>
                              <option value="2">2nd Grade</option>
                              <option value="3">3rd Grade</option>
                              <option value="4">4th Grade</option>
                              <option value="5">5th Grade</option>
                              <option value="6">6th Grade</option>
                              <option value="7">7th Grade</option>
                              <option value="8">8th Grade</option>
                              <option value="9">9th Grade</option>
                              <option value="10">10th Grade</option>
                              <option value="11">11th Grade</option>
                              <option value="12">12th Grade</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Parent/Guardian Information */}
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-4">Parent/Guardian Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="parentFirstName">First Name *</Label>
                            <Input
                              id="parentFirstName"
                              name="parentFirstName"
                              value={formData.parentFirstName}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="parentLastName">Last Name *</Label>
                            <Input
                              id="parentLastName"
                              name="parentLastName"
                              value={formData.parentLastName}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone">Phone *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <Label htmlFor="address">Address</Label>
                          <Input
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div>
                            <Label htmlFor="city">City</Label>
                            <Input
                              id="city"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="state">State</Label>
                            <Input
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="zip">ZIP Code</Label>
                            <Input
                              id="zip"
                              name="zip"
                              value={formData.zip}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Emergency Contact */}
                      <div className="bg-stone-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-stone-900 mb-4">Emergency Contact</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="emergencyContactName">Contact Name *</Label>
                            <Input
                              id="emergencyContactName"
                              name="emergencyContactName"
                              value={formData.emergencyContactName}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="emergencyContactPhone">Contact Phone *</Label>
                            <Input
                              id="emergencyContactPhone"
                              name="emergencyContactPhone"
                              type="tel"
                              value={formData.emergencyContactPhone}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="emergencyContactRelation">Relationship</Label>
                            <Input
                              id="emergencyContactRelation"
                              name="emergencyContactRelation"
                              placeholder="e.g., Grandmother"
                              value={formData.emergencyContactRelation}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Medical/Special Needs */}
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-4">Additional Information</h3>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="allergies">Allergies (if any)</Label>
                            <Input
                              id="allergies"
                              name="allergies"
                              value={formData.allergies}
                              onChange={handleChange}
                              placeholder="List any allergies"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="medicalConditions">Medical Conditions or Special Needs</Label>
                            <Textarea
                              id="medicalConditions"
                              name="medicalConditions"
                              value={formData.medicalConditions}
                              onChange={handleChange}
                              placeholder="Please list any conditions the instructor should be aware of"
                              className="mt-1"
                              rows={2}
                            />
                          </div>
                          <div>
                            <Label htmlFor="howHeard">How did you hear about us?</Label>
                            <select
                              id="howHeard"
                              name="howHeard"
                              value={formData.howHeard}
                              onChange={handleChange}
                              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md"
                            >
                              <option value="">Select one</option>
                              <option value="kahl_radio">KAHL Radio</option>
                              <option value="kpac_radio">KPAC Radio</option>
                              <option value="friend">Friend/Family</option>
                              <option value="school">School</option>
                              <option value="social_media">Social Media</option>
                              <option value="google">Google Search</option>
                              <option value="returning">Returning Student</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Newsletter Opt-in */}
                      <NewsletterOptIn 
                        checked={formData.newsletterOptIn}
                        onChange={(checked) => setFormData({...formData, newsletterOptIn: checked})}
                      />
                        </>
                      ) : (
                        /* ── Adult class form ── */
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="parentFirstName">First Name *</Label>
                              <Input id="parentFirstName" name="parentFirstName" value={formData.parentFirstName} onChange={handleChange} className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="parentLastName">Last Name *</Label>
                              <Input id="parentLastName" name="parentLastName" value={formData.parentLastName} onChange={handleChange} className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="email">Email Address *</Label>
                              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className="mt-1" required />
                            </div>
                            <div>
                              <Label htmlFor="phone">Phone Number *</Label>
                              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="mt-1" required />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="howHeard">How did you hear about us?</Label>
                            <select id="howHeard" name="howHeard" value={formData.howHeard} onChange={handleChange} className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-md">
                              <option value="">Select one</option>
                              <option value="kahl_radio">KAHL Radio</option>
                              <option value="kpac_radio">KPAC Radio</option>
                              <option value="friend">Friend/Family</option>
                              <option value="google">Google Search</option>
                              <option value="social_media">Social Media</option>
                              <option value="returning">Returning Student</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <NewsletterOptIn
                            checked={formData.newsletterOptIn}
                            onChange={(checked) => setFormData({...formData, newsletterOptIn: checked})}
                          />
                        </>
                      )}

                      {/* Payment Section */}
                      <div className="pt-6 border-t border-stone-200">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-lg font-semibold text-stone-900">Total:</span>
                          <span className="text-2xl font-bold text-cafa-red">${getPrice()}</span>
                        </div>

                        {!showPayPal ? (
                          <Button 
                            type="submit" 
                            className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white py-6 text-lg"
                            data-testid="proceed-to-payment-btn"
                          >
                            Proceed to Payment
                          </Button>
                        ) : (
                          <div className="space-y-4">
                            <p className="text-sm text-stone-600 text-center">Complete your payment with PayPal:</p>
                            <PayPalCheckout
                              items={getClassItems()}
                              registrationType="class"
                              payerEmail={formData.email}
                              payerName={`${formData.parentFirstName} ${formData.parentLastName}`.trim()}
                              registrationData={{
                                program_id: classProgram.id,
                                program_name: classProgram.title,
                                instructor: classProgram.instructor,
                                schedule: isPrivate ? 'By appointment' : classProgram.schedule,
                                scheduling_mode: isPrivate ? 'by_appointment' : 'term',
                                session_option: isPrivate ? formData.sessionOption : undefined,
                                preferred_times: isPrivate ? formData.preferredTimes : undefined,
                                student_name: `${formData.studentFirstName} ${formData.studentLastName}`.trim(),
                                student_age: formData.studentAge,
                                student_grade: formData.studentGrade,
                                parent_name: `${formData.parentFirstName} ${formData.parentLastName}`.trim(),
                                email: formData.email,
                                phone: formData.phone,
                                address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
                                emergency_contact: {
                                  name: formData.emergencyContactName,
                                  phone: formData.emergencyContactPhone,
                                  relation: formData.emergencyContactRelation
                                },
                                allergies: formData.allergies,
                                medical_conditions: formData.medicalConditions,
                                how_heard: formData.howHeard,
                                newsletter_opt_in: formData.newsletterOptIn
                              }}
                              onSuccess={handlePaymentSuccess}
                              onError={handlePaymentError}
                            />
                            <Button 
                              type="button"
                              variant="outline" 
                              className="w-full"
                              onClick={() => setShowPayPal(false)}
                            >
                              Back to Edit Form
                            </Button>
                          </div>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegisterClass;
